import { CommonModule } from '@angular/common';
import { Component, computed, Inject, inject, Optional, signal, Signal, ViewChild, WritableSignal, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, NgForm, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { CalendarEvent, CalendarEventSetup, CalendarIntegration } from '@app/_interfaces/calendar-integration.interface';
import { EntitySetup } from '@app/_interfaces/feature.interface';
import { AlertService } from '@app/_services/alert.service';
import { CacheService } from '@app/_services/cache.service';
import { CalendarIntegrationStore } from '@app/_stores';
import { CalendarIntegrationType } from '@app/_types/generic.type';
import { InputDatePeriodComponent } from '@app/components/shared/inputs/input-date-period/input-date-period.component';
import { InputReminderComponent } from '@app/components/shared/inputs/input-reminder/input-reminder.component';
import { InputTextComponent } from '@app/components/shared/inputs/input-text/input-text.component';
import { InputTimeComponent } from '@app/components/shared/inputs/input-time/input-time.component';
import { SvgComponent } from '@app/components/shared/svg/svg.component';
import { Subject, takeUntil } from 'rxjs';
import { CalendarAccountFormComponent } from '../calendar-account-form/calendar-account-form.component';
import { SettingsService } from '@app/_services/settings.service';
import { AnalyticsService } from '@app/_services/analytics.service';
import { MapService } from '@app/_services/map.service';
import { AnalyticsScreen, SubFeatureActionValue } from '@app/_types/analytics-events.types';
import { StorageService } from '@app/_services/storage.servive';
import { QuillModule } from 'ngx-quill';
import { getCustomDate } from '@app/_services/date';
import { CalendarIntegrationService } from '@app/_services/features/calendar-integration.service';
import { CustomDate } from '@app/_interfaces/generic.interface';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { ModalService } from '@app/_services/modal.service';

@Component({
  selector: 'app-calendar-event-form',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatMenuModule,
    SvgComponent,
    InputTextComponent,
    InputDatePeriodComponent,
    InputTimeComponent,
    InputReminderComponent,
    QuillModule
  ],
  templateUrl: './calendar-event-form.component.html',
  styleUrl: './calendar-event-form.component.scss'
})

export class CalendarEventFormComponent implements OnInit, OnDestroy {

  @ViewChild('cEventForm') cEventForm!: NgForm;
  unSubscribe = new Subject<void>();
  calendarEventForm!: FormGroup;
  selectedDate: Signal<Date>;
  // calendarEventInitial: CalendarEventSetup;
  readonly calendarStore = inject(CalendarIntegrationStore);
  // calendarType: WritableSignal<CalendarIntegrationType> = signal('google');
  // rawData: {
  //   hangoutLink?: string,
  //   showAs?: string,
  //   sensitivity?: string,
  //   onlineMeetingUrl?: string,
  //   onlineMeeting?: { joinUrl: string },
  //   id?: string,
  // };
  // calendarAccount: CalendarIntegration;
  calendarTypeString: Signal<SubFeatureActionValue> = computed(() => {
    return this.mapService.subTypesMap()[this.calendarType()];
  });
  screenEnterTime: Signal<Date> = signal(new Date());
  dueDate: Signal<Date | null> = computed(() => {
    return this.data.dateString ? new Date(this.data.dateString) : null;
  });
  eventSetup: Signal<CalendarEventSetup | null> = computed(() => {
    return this.calendarStore.idToEventSetup()[this.data.value.id] || null;
  });
  calendarType: Signal<CalendarIntegrationType> = computed(() => {
    return this.eventSetup()?.type || 'google';
  });

  eventData: Signal<CalendarEvent> = computed(() => {
    const setup = this.eventSetup();
    if (!setup) return {} as CalendarEvent;
    const dateString = this.data.dateString;
    const calendarIntegrationId = setup.calendarIntegrationId;
    const eventMap = this.calendarStore.eventMap();
    const eventDataFromMap: CalendarEvent | null = eventMap[calendarIntegrationId]?.[setup.id]?.[dateString] || null;
    // const constructedEvent = this.setupToCalendarEvents(setup);
    const eventData = eventDataFromMap ? { ...(eventDataFromMap as CalendarEvent), repeat: setup.repeat } : this.calendarService.setupToCalendarEvents(setup);
    this.setCalendarForm(eventData);
    return eventData;
  });
  rawData: Signal<{
    hangoutLink?: string,
    showAs?: string,
    sensitivity?: string,
    onlineMeetingUrl?: string,
    onlineMeeting?: { joinUrl: string },
    id?: string,
  }> = computed(() => {
    const rawData = this.eventData()?.rawData || '{}';
    return JSON.parse(rawData);
  });
  calendarAccount: Signal<CalendarIntegration> = computed(() => {
    const calendarIntegrationId = this.eventSetup()?.calendarIntegrationId || '';
    return this.calendarStore.calIdToAccount()[calendarIntegrationId];
  });

  data: { mode: 'new' | 'edit', value: EntitySetup, dateString: string };

  constructor(
    private fb: FormBuilder,
    @Optional() public dialogRef: MatDialogRef<CalendarEventFormComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<CalendarEventFormComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: { mode: 'new' | 'edit', value: EntitySetup, dateString: string },
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { mode: 'new' | 'edit', value: EntitySetup, dateString: string },
    public cc: CacheService,
    private alertService: AlertService,
    public dialog: MatDialog,
    public ss: SettingsService,
    private mapService: MapService,
    private as: AnalyticsService,
    private storageService: StorageService,
    private calendarService: CalendarIntegrationService,
    private modalService: ModalService,
  ) {
    this.data = dialogData || bottomSheetData;
    this.calendarEventForm = this.fb.group({
      id: new FormControl(''),
      title: new FormControl(''),
      description: new FormControl(''),
      startAt: new FormControl(getCustomDate()),
      endAt: new FormControl(null),
      isStartTimeSet: new FormControl(false),
      isTzAffected: new FormControl(false),
      duration: new FormControl(15),
      reminderAt: new FormControl([]),
      repeat: new FormControl(["RRULE:FREQ=DAILY"]),
      location: new FormControl(''),
    });

    this.selectedDate = computed(() => {
      return this.data.dateString ? new Date(this.data.dateString) : new Date();
    });
    // const calendarEvent = this.calendarStore.idToEventSetup()[data.value.id];
    // console.log("calendare event ====================--->", calendarEvent)
    // this.calendarEventInitial = this.initiateForm(calendarEvent);
    // this.calendarType.set(calendarEvent.type);
    // this.rawData = JSON.parse(calendarEvent.rawData);
    // console.log("rawData ====================--->", this.rawData);
    // this.calendarAccount = this.calendarStore.calIdToAccount()[calendarEvent.calendarIntegrationId];
    // console.log("calendarAccount ====================--->", this.calendarAccount);

    // this.calendarEventForm = this.fb.group({
    //   id: new FormControl(this.calendarEventInitial.id, Validators.required),
    //   title: new FormControl(this.calendarEventInitial.title, Validators.required),
    //   description: new FormControl(this.calendarEventInitial.description),
    //   startAt: new FormControl(this.calendarEventInitial.startAt),
    //   endAt: new FormControl(this.calendarEventInitial.endAt),
    //   isStartTimeSet: new FormControl(this.calendarEventInitial.isStartTimeSet, Validators.required),
    //   isTzAffected: new FormControl(this.calendarEventInitial.isTzAffected, Validators.required),
    //   duration: new FormControl(this.calendarEventInitial.duration, Validators.required),
    //   reminderAt: new FormControl(this.calendarEventInitial.reminderAt, Validators.required),
    //   repeat: new FormControl(this.calendarEventInitial.repeat, Validators.required),
    //   location: new FormControl(this.calendarEventInitial.location),
    // });
    const eventData = this.eventData();
    this.setCalendarForm(eventData);
    console.log("eventData ====================--->", eventData);
    // console.log("calendarEventForm ====================--->", this.calendarEventForm);
    // console.log("rawData ====================--->", this.rawData);
    // console.log("calendarAccount ====================--->", this.calendarAccount);
    console.log("eventSetup ====================--->", this.eventSetup());

    dialogRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });

    bottomSheetRef?.backdropClick().pipe(takeUntil(this.unSubscribe)).subscribe(async () => {
      this.closeDialog();
    });
  }

  ngOnInit() {
    const previousScreen = this.storageService.getCurrentScreen() as AnalyticsScreen;
    this.as.screenViewEvent('calendarAction', previousScreen, this.calendarTypeString());
    if (this.data.mode === 'edit') {
      this.as.crudEvent('read', 'calendarAction', 'private', 0, 'notShared', 'owner', this.calendarTypeString());
    }
  };

  setCalendarForm(calendarEvent: CalendarEvent) {
    // const getStartAt = this.getStartAt(calendarEvent.startAt);
    this.calendarEventForm.get('id')?.setValue(calendarEvent.id);
    this.calendarEventForm.get('title')?.setValue(calendarEvent.title);
    this.calendarEventForm.get('description')?.setValue(calendarEvent.description);
    this.calendarEventForm.get('startAt')?.setValue(calendarEvent.startAt);
    this.calendarEventForm.get('endAt')?.setValue(calendarEvent.endAt);
    this.calendarEventForm.get('isStartTimeSet')?.setValue(calendarEvent.isStartTimeSet);
    this.calendarEventForm.get('isTzAffected')?.setValue(calendarEvent.isTzAffected);
    this.calendarEventForm.get('duration')?.setValue(calendarEvent.duration);
    this.calendarEventForm.get('reminderAt')?.setValue(calendarEvent.reminderAt);
    this.calendarEventForm.get('repeat')?.setValue(calendarEvent.repeat);
    this.calendarEventForm.get('location')?.setValue(calendarEvent.location);
  };

  // getStartAt(date: CustomDate): CustomDate {
  //   return {
  //     dateString: date.dateString,
  //     timeString: getFormatTime(date.dateTime),
  //     dateTime: date.dateTime,
  //     timeWithOffset: date.timeWithOffset,
  //   }
  // };

  initiateForm(calendarEvent: CalendarEventSetup): any {
    return {
      id: calendarEvent.id,
      title: calendarEvent.title,
      description: calendarEvent.description,
      calendarId: calendarEvent.calendarId,
      calendarName: calendarEvent.calendarName,
      calendarIntegrationId: calendarEvent.calendarIntegrationId,
      startAt: calendarEvent.startAt,
      endAt: calendarEvent.endAt,
      cloudUpdatedAt: calendarEvent.cloudUpdatedAt,
      isStartTimeSet: calendarEvent.isStartTimeSet,
      isTzAffected: calendarEvent.isTzAffected,
      duration: calendarEvent.duration,
      reminderAt: calendarEvent.reminderAt,
      repeat: calendarEvent.repeat,
      location: calendarEvent.location,
    }
  }

  getFc(fcName: string): FormControl {
    //convert to htmtstring to textstring



    return this.calendarEventForm.get(fcName) as FormControl;
  }

  getdec(fcName: string): any {
    const description = this.calendarEventForm.get(fcName)?.value || '';

    let parsedDescription;

    // If description is already a Delta/JSON object, use as is
    if (typeof description === 'object') {
      parsedDescription = description;
    }
    // If description is a string, try to parse it
    else if (typeof description === 'string') {
      try {
        parsedDescription = JSON.parse(description);
      } catch {
        // If not valid JSON, return as plain text
        return description;
      }
    } else {
      return description;
    }

    // Remove color and background attributes from Delta format
    if (Array.isArray(parsedDescription)) {
      parsedDescription = parsedDescription.map(op => {
        if (op.attributes) {
          const cleanAttributes = { ...op.attributes };
          delete cleanAttributes.color;
          delete cleanAttributes.background;
          return { ...op, attributes: Object.keys(cleanAttributes).length > 0 ? cleanAttributes : undefined };
        }
        return op;
      });
    }

    return parsedDescription;
  }

  // getdec(fcName: string): string {
  //   const html = this.calendarEventForm.get(fcName)?.value || '';

  //   const tempDiv = document.createElement('div');
  //   tempDiv.innerHTML = html;
  //   const text = tempDiv.textContent || tempDiv.innerText || '';

  //   console.log(text);
  //   return text;
  // }

  async openAlert() {
    // const confirm = this.alertService.alert(this.ss.texts()['editCalendarEventAccessDenied_title'], this.ss.texts()['editCalendarEventAccessDenied_content'], this.ss.texts()['common_ok']);
    const confirm = await this.alertService.confirmRevamped({
      title: this.ss.texts()['editEvent_title'],
      description: this.calendarType() === 'google' ? this.ss.texts()['editEvent_calendarGoogleContent'] : this.ss.texts()['editEvent_calendarOutlookContent'],
      yesButtonText: this.ss.texts()['common_edit'],
      noButtonText: this.ss.texts()['common_buttonCancel'],
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (!confirm) return;
    const eventId = this.rawData()?.id;
    if (!eventId) return;
    this.openCalendarEventInApp(eventId);
  };

  async openLocation() {
    const confirm = await this.alertService.confirmRevamped({
      title: this.ss.texts()['openInMap_title'],
      description: this.ss.texts()['openInMap_content'],
      yesButtonText: this.ss.texts()['openInMap_map'],
      noButtonText: this.ss.texts()['common_edit'],
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (confirm) {
      this.routeToLocation();
    } else if (confirm === false) {
      const eventId = this.rawData()?.id;
      if (!eventId) return;
      this.openCalendarEventInApp(eventId);
    }
  };

  routeToLocation() {
    const location = this.eventData()?.location;
    if (!location) return;

    const encodedLocation = encodeURIComponent(location);
    const webMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;

    // Deep links for mobile apps
    const iosDeepLink = `comgooglemaps://?q=${encodedLocation}`;
    const androidIntent = `intent://maps.google.com/maps?q=${encodedLocation}#Intent;package=com.google.android.apps.maps;scheme=https;end`;

    const userAgent = navigator.userAgent || navigator.vendor;

    if (/android/i.test(userAgent)) {
      // Try opening the Google Maps Android app
      window.location.href = androidIntent;

      // Fallback to web if app not available
      setTimeout(() => {
        window.open(webMapsUrl, '_blank');
      }, 800);

    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      // Try opening the Google Maps iOS app
      window.location.href = iosDeepLink;

      // Fallback to web after short delay
      setTimeout(() => {
        window.open(webMapsUrl, '_blank');
      }, 800);

    } else {
      // Desktop fallback — open Google Maps in browser
      window.open(webMapsUrl, '_blank');
    }
  }

  openCalendarEventInApp(eventId: string) {
    if (this.calendarType() === 'google') {
      this.openGoogleCalendarEvent(eventId);
    } else if (this.calendarType() === 'microsoft') {
      this.openOutlookCalendarEvent(eventId);
    }
  }

  openGoogleCalendarEvent(eventId: string) {
    const calendarId = this.eventData()?.calendarId;
    const cleanEventId = eventId.split('@')[0];
    const eidString = `${cleanEventId} ${calendarId}`;
    const eidEncoded = btoa(eidString).replaceAll('=', '');
    const deepLinkUrl = `https://www.google.com/calendar/event?eid=${eidEncoded}`;

    // App deep link (Android)
    const androidIntent = `intent://www.google.com/calendar/event?eid=${eidEncoded}#Intent;package=com.google.android.calendar;scheme=https;end`;

    // iOS deep link (uses universal link)
    const iosDeepLink = `googlecalendar://www.google.com/calendar/event?eid=${eidEncoded}`;

    const userAgent = navigator.userAgent || navigator.vendor;

    if (/android/i.test(userAgent)) {
      // Try to open Google Calendar app
      window.location.href = androidIntent;

      // Fallback to web after short delay
      setTimeout(() => {
        window.open(deepLinkUrl, '_blank');
      }, 1000);

    } else if (/iPad|iPhone|iPod/.test(userAgent)) {
      // Try to open Calendar app (iOS)
      window.location.href = iosDeepLink;

      // Fallback to web
      setTimeout(() => {
        window.open(deepLinkUrl, '_blank');
      }, 1000);

    } else {
      // Desktop or unsupported → open web
      window.open(deepLinkUrl, '_blank');
    }
  };

  async openMeetLink() {
    const confirm = await this.alertService.confirmRevamped({
      title: this.ss.texts()['joinEvent_title'],
      description: this.ss.texts()['joinEvent_content'],
      yesButtonText: this.ss.texts()['joinEvent_join'],
      noButtonText: this.ss.texts()['common_edit'],
      yesButtonColor: 'color-35',
      noButtonColor: 'color-7',
    });
    if (confirm) {
      const meetLink = this.rawData()?.hangoutLink || this.rawData()?.onlineMeetingUrl || this.rawData()?.onlineMeeting?.joinUrl;
      if (!meetLink) return;
      window.open(meetLink, '_blank');
    } else if (confirm === false) {
      const eventId = this.rawData()?.id;
      if (!eventId) return;
      this.openCalendarEventInApp(eventId);
    }
  }

  openOutlookCalendarEvent(eventId: string) {
    const personalDomains = ['outlook.com', 'hotmail.com', 'live.com'];
    const isPersonalAccount = personalDomains.some(domain => this.calendarAccount().email.includes(domain));
    const encodedEventId = encodeURIComponent(eventId);
    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const outlookUrls = this.buildOutlookUrls(encodedEventId, isPersonalAccount, isMobile);
    if (isMobile) {
      // Try app deep link first, then fallback after 2 seconds if app doesn't open
      const appUrl = outlookUrls[0];
      const webUrl = outlookUrls[1];

      const start = Date.now();
      window.location.href = appUrl;

      setTimeout(() => {
        const elapsed = Date.now() - start;
        // If user is still here after 2s, the app didn't open
        if (elapsed < 2100) {
          window.open(webUrl, '_blank');
        }
      }, 2000);
    } else {
      // Desktop → Open directly in browser
      for (const url of outlookUrls.slice(1)) {
        const win = window.open(url, '_blank');
        if (win) break;
      }
    }
  };

  buildOutlookUrls(eventId: string, isPersonalAccount: boolean, isMobile: boolean) {
    const baseDomain = isPersonalAccount ? 'outlook.live.com' : 'outlook.office365.com';

    const urls: string[] = [];

    // ✅ Try mobile app deep link *only on mobile*
    if (isMobile) {
      urls.push(`ms-outlook://events/show?itemid=${eventId}`);
    }

    // ✅ Web versions (for all platforms)
    urls.push(
      `https://${baseDomain}/calendar/item/${eventId}`, // New Outlook web
      `https://${baseDomain}/owa/?itemid=${eventId}&exvsurl=1&path=/calendar/item`, // Older Outlook web
      `https://${isPersonalAccount ? 'outlook.live.com' : 'outlook.office.com'}/calendar` // Fallback calendar
    );

    return urls;
  }

  openCalendarAccountForm() {
    if (this.cc.isNarrowScreen()) {
      const setupCalendarDialog = this.modalService.openMainContent(CalendarAccountFormComponent,
        {
          mode: 'edit',
          value: this.calendarAccount,
        },
        { disableClose: true, autoFocus: false }
      );
    } else {
      const setupCalendarDialog = this.modalService.openModalLevel1(CalendarAccountFormComponent,
        {
          mode: 'edit',
          value: this.calendarAccount,
        },
        { disableClose: true, autoFocus: false }
      );
    }
    // return setupCalendarDialog.afterClosed().pipe(takeUntil(this.unSubscribe)).subscribe(result => {
    //   if (result) {
    //     this.close();
    //   }
    // });;
  }

  async closeDialog() {
    this.close();
  }

  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }

  ngOnDestroy() {
    this.as.screenExitEvent('calendarAction', this.screenEnterTime(), this.calendarTypeString());
    this.unSubscribe.complete();
    this.unSubscribe.next();
  }
}
